/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  Component,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import {makeStyles} from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {info} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';
import {useMedia} from '../../hooks/useMedia';
import Hidden from '../../layout/Hidden';
import Render from '../../render/Render';
import {dlsAlt, dlsColor, dlsLayout, dlsShadow, dlsZIndex} from '../../theme/theme';
import {DLSThemes} from '../../theme/types';
import useThemeContext from '../../theme/useThemeContext';
import {MenuItem, MenuItems} from '../../types/components';
import {SideSheetView} from '../../types/types';
import SideSheet from '../sidesheet/SideSheet';
import {Navigation as CurrentNavigation} from './current/Navigation';
import {FLYOUT_SUBMENU_ITEM_THRESHOLD} from './constants';
import NavigationItem from './NavigationItem';
import NavigationSwitcher from './NavigationSwitcher';
import useExpandCollapse, {toggleCollapseStyle} from './useExpandCollapse';
import navStyles from './Navigation.module.scss';

export interface SideNavProps {
  onToggleCollapse?: Function;
};

// defines the props for primary and secondary navigation containers
export interface NavigationTypes extends SideNavProps {
  menuItems: MenuItems,
  // [DLS PACKAGE TODO]: Compact arguments to typed parameters object
  onSetPage(page: string, index: number, isSecondaryIndex?: boolean, item?: MenuItem, isMainMenu?: boolean): void;
  selectedIndex: number;
  activeIndex?: number;
  overrideStyle: GenericObject;
  hasMultipleSubModule: boolean,
  sideSheetContent?: () => JSX.Element;
  sideSheetMobileSwipeUp?: Component | unknown;
  sideSheetStatus?: {
    activeSideSheet: null | string;
    desktopSideSheetOpen: boolean;
    mobileSideSheetStatus: 'closed' | 'half' | 'open';
  };
  toggleSideSheet?: Function;
  geoFencePanelOpen?: boolean;
  showFindNearByPopup?: boolean;
  isUserProfileView?: boolean;
  useExpandCollapseFeature?: boolean;
  findButtonStatus?: boolean;
  isInitialized?: Boolean;
  isLoaded?: Boolean;
  secondaryNav?: {
    pages: MenuItems;
    show: boolean;
  };
  closeSecondaryNavigation?: () => void;
  currentPage: string;
  getPages: Function;
  isSideSheetArrowVisible?: boolean;
  sideSheetView?: SideSheetView
  isMobileFilterActive?: boolean;
  fullHeight?: boolean;
  isiOn?: boolean;
  isDrawerActive?: boolean;
  toolTipArrow?: boolean;
  toolTipPosition?: string;
  isCtcAdmin?: boolean;
  fullHeightIcons?: boolean;
  headerContent?: ReactNode | ((collapsed: boolean) => ReactNode);
  mobileHeaderContent?: ReactNode | ((collapsed: boolean, onClose?: () => void) => ReactNode);
  footerItems?: MenuItems;
  footerContent?: ReactNode | ((collapsed: boolean) => ReactNode);
  navigationVariant?: DLSThemes;
  onTopNavigationVisibilityChange?: (height: number | null) => void;
  forceCollapsed?: boolean;
  renderedPage?: string;
};

interface StyleProps {
  fullHeight?: boolean,
  isPhone?: boolean,
  collapsed?: boolean,
  useExpandCollapseFeature?: boolean,
  navigationVariant?: DLSThemes
};

const getPaperMinWidth = (collapsed) => {
  const collapsedNavWidth = dlsLayout('collapsedSidebar').width;
  const expandedNavWidth  = dlsLayout('sidebar').width;
  const toggleFunc        = toggleCollapseStyle(collapsedNavWidth, expandedNavWidth);
  return toggleFunc({collapsed});
};

// defines the styles used by primary navigation container
const useStyles = makeStyles((theme) => ({
  root : {
    display : 'flex'
  },
  sideNavRoot : {
    zIndex : `${dlsZIndex('sideNavIndex')} !important` as any
  },
  nonUserProfileView : {
    position : 'fixed',
    top      : 2,
    left     : 12,
    zIndex   : dlsZIndex('navigationNonUserProfileView')
  },
  // current-theme mobile Top Navigation (Figma "Navigation/Secondary/Mweb"):
  // 44x44 Menu target at x16, vertically centered in the mobile header ...
  currentMenuContainer : {
    left : 16,
    top  : (dlsLayout('header').mobile - 44) / 2
  },
  currentMenuButton : {
    borderRadius : 4,
    color        : dlsColor('grey2'),
    margin       : 0,
    padding      : 10,
    '&:hover'    : {
      backgroundColor : 'transparent'
    }
  },
  currentMenuButtonPressed : {
    color : dlsColor('darkTeal')
  },
  currentMenuFrame : {
    alignItems     : 'center',
    borderRadius   : 4,
    display        : 'flex',
    height         : 24,
    justifyContent : 'center',
    width          : 24
  },
  currentMenuFramePressed : {
    backgroundColor : dlsAlt('colors', 'paleBlue2'),
    boxShadow       : dlsShadow(3)
  },
  drawerPaper : {
    '&&' : {
      overflowX       : 'hidden',
      overflowY       : 'initial',
      backgroundColor : ({navigationVariant}: StyleProps) =>
        `${dlsColor(navigationVariant === 'current' ? 'white' : 'grey1')} !important`,
      height                         : '100vh',
      zIndex                         : dlsZIndex('navigationDrawerPaper'),
      transition                     : 'width 0.2s ease-in-out',
      [theme.breakpoints.down('sm')] : {
        top    : `${dlsLayout('header').mobile}px !important`,
        height : ({fullHeight, isPhone}: StyleProps) => fullHeight && isPhone ? 'calc(100vh - 48px)' : 'calc(100vh - 56px)',
        width  : ({navigationVariant}: StyleProps) =>
          navigationVariant === 'current' ? dlsLayout('sidebar').width : undefined
      },
      [theme.breakpoints.up('md')] : {
        top : ({navigationVariant}: StyleProps) =>
          `${navigationVariant === 'current' ? 0 : dlsLayout('header').desktop}px !important`,
        width : ({useExpandCollapseFeature, collapsed}: StyleProps) =>
          useExpandCollapseFeature
            ? getPaperMinWidth(collapsed)
            : dlsLayout('sidebar').width
      }
    }
  },
  wrapper : {
    display        : 'flex',
    justifyContent : 'space-between',
    flexDirection  : 'column',
    height         : '100%'
  },
  drawerPaperAnchorLeft : {
    borderRight : ({navigationVariant}: StyleProps) =>
      navigationVariant === 'current' ? `1.5px solid ${dlsColor('pastelTeal')}` : 'none'
  },
  content : {
    flexGrow : 1,
    padding  : dlsAlt('layout', 'spacing')
  }
}));

/**
 * @Navigation
 *
 * Defines the Navigation Component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   const navItems = [
 *     {icon: <Page1Icon>, label: t('page1:title), pages: '/pages1' },
 *     {icon: <Page2Icon>, label: t('page2:title), pages: '/pages2' },
 *   ];
 *
 *    <Navigation menuItems={navItems}
 *      onSetPage={onSetPage}
 *      selectedIndex={selectedIndex}
 *    />
 *
 * ```
 */
const Navigation = ({
  menuItems,
  onSetPage,
  selectedIndex,
  activeIndex,
  hasMultipleSubModule,
  sideSheetContent,
  sideSheetStatus,
  toggleSideSheet,
  geoFencePanelOpen,
  showFindNearByPopup,
  isUserProfileView,
  findButtonStatus,
  isInitialized,
  isLoaded,
  secondaryNav,
  closeSecondaryNavigation,
  currentPage,
  getPages,
  overrideStyle,
  isSideSheetArrowVisible,
  sideSheetView,
  isMobileFilterActive,
  sideSheetMobileSwipeUp,
  isiOn,
  onToggleCollapse,
  fullHeight = false,
  useExpandCollapseFeature = false,
  isDrawerActive = true,
  toolTipPosition = 'bottom',
  toolTipArrow = false,
  isCtcAdmin = false,
  fullHeightIcons = false,
  headerContent,
  mobileHeaderContent,
  footerItems = [],
  footerContent,
  forceCollapsed = false,
  onTopNavigationVisibilityChange,
  renderedPage,
  navigationVariant = DLSThemes.legacy
}: NavigationTypes) => {
  const {isDesktop, isPhone}        = useMedia();
  const {isCurrent}                 = useThemeContext();
  const wasForcedRef                = useRef(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuPressed, setMenuPressed] = useState(false);
  const showSecondaryNavigation     = secondaryNav?.show;

  const isSideSheetValid = (
    (!isiOn || !isDesktop) &&
    !isUserProfileView &&
    isDrawerActive
  );

  const {
    classes: expandClasses,
    collapsed,
    onHandleCollapse
  } = useExpandCollapse(useExpandCollapseFeature, onToggleCollapse);

  // current theme: a collapse forced by an open drawer (and its release) is
  // not saved to the user's preferences - only their own toggle is, so a
  // refresh with a drawer open keeps the nav as the user left it. The legacy
  // theme saves it, as before ...
  useEffect(() => {
    if (forceCollapsed && !collapsed) {
      wasForcedRef.current = true;
      onHandleCollapse(null, true, !isCurrent);
    } else if (isCurrent && !forceCollapsed && wasForcedRef.current) {
      wasForcedRef.current = false;
      onHandleCollapse(null, false, false);
    }
  }, [collapsed, forceCollapsed, onHandleCollapse, isCurrent]);

  const classes = useStyles({
    fullHeight,
    isPhone,
    collapsed,
    useExpandCollapseFeature,
    navigationVariant
  });

  const handleDrawerToggle = function() {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuPressStart = useCallback(() => {
    setMenuPressed(true);
  }, []);

  const handleMenuPressEnd = useCallback(() => {
    setMenuPressed(false);
  }, []);

  const closeMobileDrawer = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const container =
    window !== undefined
      ? function() {
        return window.document.body;
      }
      : undefined;

  const NavigationAccent = () => (
    <div style={{display: 'block'}}>
      <div className={navStyles.accent} />
      <div className={navStyles.bottomAccent} />
    </div>
  );

  // [DLS PACKAGE TODO]: Refactor to parameters object
  // eslint-disable-next-line max-params
  const setPage = useCallback(function(page: string, index: number, isSecondaryIndex?: boolean,
    item?: MenuItem, isMainMenu?: boolean) {
    const unifiedPages  = page.includes('maintenance') || page.includes('crashboxx') ||
    page.includes('eld');
    const isCurrentMobileTopNavigationParent = (
      navigationVariant === 'current' &&
      !isDesktop &&
      !isSecondaryIndex &&
      (item?.pages?.length || 0) >= FLYOUT_SUBMENU_ITEM_THRESHOLD
    );
    if (!isDesktop && (!item?.pages || unifiedPages || isCurrentMobileTopNavigationParent)) {
      setMobileOpen(false);
    }
    info('page changing to:', page);
    const shouldTreatAsSecondary = (
      navigationVariant === 'current' ? !!isSecondaryIndex : !!(isSecondaryIndex || showSecondaryNavigation)
    );
    onSetPage(page, index, shouldTreatAsSecondary, item, isMainMenu);
  }, [onSetPage, isDesktop, navigationVariant, showSecondaryNavigation]);

  const isCurrentTheme   = navigationVariant === 'current';
  const isCurrentDesktop = isCurrentTheme && isDesktop;
  const isCurrentMobile  = isCurrentTheme && !isDesktop;

  const secondaryMenuItems = useMemo(() => {
    if (!showSecondaryNavigation) {
      return [];
    }
    return getPages(menuItems[activeIndex]?.pages || secondaryNav?.pages, currentPage) as unknown as MenuItems;
  }, [showSecondaryNavigation, getPages, menuItems, activeIndex, secondaryNav, currentPage]);
  const mobileFooterItems = useMemo(() => (
    isCurrentMobile ? footerItems.filter(({id}) => id === 'help') : footerItems
  ), [footerItems, isCurrentMobile]);

  return (
    <div className={classes.root}
      style={{marginTop: isCurrentDesktop ? 0 : (isDesktop ? dlsLayout('header').height : 0)}}>
      {isCurrentDesktop ? (
        <CurrentNavigation activeIndex={activeIndex}
          collapsed={collapsed}
          closeSecondaryNavigation={closeSecondaryNavigation}
          currentPage={currentPage}
          drawerPaperClassName={classes.drawerPaper}
          drawerPaperAnchorLeftClassName={classes.drawerPaperAnchorLeft}
          footerContent={footerContent}
          footerItems={footerItems}
          headerContent={headerContent}
          menuItems={menuItems}
          onHandleCollapse={onHandleCollapse}
          onTopNavigationVisibilityChange={onTopNavigationVisibilityChange}
          onSetPage={setPage}
          renderedPage={renderedPage}
          secondaryMenuItems={secondaryMenuItems}
          selectedIndex={selectedIndex}
          showSecondaryNavigation={showSecondaryNavigation}
          useExpandCollapseFeature={useExpandCollapseFeature} />
      ) : (
        <>
          <Hidden show='mobile'>
            {!isUserProfileView &&
        <div className={classnames(classes.nonUserProfileView, {
          [classes.currentMenuContainer] : isCurrentTheme
        })}>
          <Render>
            <Render.If condition={isCurrentTheme}>
              {/* Pressed only while the finger/pointer is down, per Figma ... */}
              <IconButton color='inherit'
                aria-label='open drawer'
                disableRipple
                className={classnames(classes.currentMenuButton, {
                  [classes.currentMenuButtonPressed] : menuPressed
                })}
                onPointerDown={handleMenuPressStart}
                onPointerUp={handleMenuPressEnd}
                onPointerLeave={handleMenuPressEnd}
                onPointerCancel={handleMenuPressEnd}
                onClick={handleDrawerToggle}>
                <span className={classnames(classes.currentMenuFrame, {
                  [classes.currentMenuFramePressed] : menuPressed
                })}>
                  <MenuIcon />
                </span>
              </IconButton>
            </Render.If>
            <Render.Else>
              <IconButton color='inherit'
                aria-label='open drawer'
                edge='start'
                onClick={handleDrawerToggle}>
                <MenuIcon />
              </IconButton>
            </Render.Else>
          </Render>
        </div>}
          </Hidden>
          <nav>
            <Hidden show='mobile'>
              {/* Render.If/Else only pair inside <Render>; a bare Render.Else
                  always renders, which mounted the legacy drawer alongside the
                  current one (two stacked backdrops) ... */}
              <Render>
                <Render.If condition={isCurrentMobile}>
                  <CurrentNavigation activeIndex={activeIndex}
                    collapsed={false}
                    closeSecondaryNavigation={closeSecondaryNavigation}
                    currentPage={currentPage}
                    drawerPaperClassName={classnames(classes.drawerPaper, navStyles.mobileNav)}
                    drawerPaperAnchorLeftClassName={classes.drawerPaperAnchorLeft}
                    drawerRootClassName={classes.sideNavRoot}
                    footerItems={mobileFooterItems}
                    menuItems={menuItems}
                    mobile
                    mobileHeaderContent={mobileHeaderContent}
                    onClose={closeMobileDrawer}
                    onHandleCollapse={onHandleCollapse}
                    onTopNavigationVisibilityChange={onTopNavigationVisibilityChange}
                    onSetPage={setPage}
                    open={mobileOpen}
                    renderedPage={renderedPage}
                    secondaryMenuItems={secondaryMenuItems}
                    selectedIndex={selectedIndex}
                    showSecondaryNavigation={showSecondaryNavigation}
                    useExpandCollapseFeature={false} />
                </Render.If>
                <Render.Else>
                  <Drawer container={container}
                    variant='temporary'
                    anchor='left'
                    open={mobileOpen}
                    onClose={closeMobileDrawer}
                    classes={{
                      paper : classnames(classes.drawerPaper, navStyles.mobileNav),
                      root  : classes.sideNavRoot
                    }}
                    ModalProps={{
                      keepMounted : true
                    }}>
                    {showSecondaryNavigation && <NavigationSwitcher label={translate('nav:navigationSwitch.ctaLabel')}
                      handleClick={closeSecondaryNavigation} />}
                    <NavigationItem fullHeight={fullHeightIcons}
                      selectedIndex={selectedIndex}
                      isCtcAdmin={isCtcAdmin}
                      activeIndex={activeIndex}
                      overrideStyle={overrideStyle}
                      hasMultipleSubModule={hasMultipleSubModule}
                      menuItems={!showSecondaryNavigation
                        ? menuItems : getPages(menuItems[activeIndex]?.pages || secondaryNav?.pages,
                          currentPage) as unknown as MenuItems}
                      onSetPage={setPage}
                      toggleSideSheet={toggleSideSheet}
                      closeSecondaryNavigation={closeSecondaryNavigation}
                      showSecondaryNavigation={showSecondaryNavigation}
                      secondaryNavOpen={null}
                      currentPage={currentPage}
                      useExpandCollapseFeature={useExpandCollapseFeature}
                      expandClasses={expandClasses}
                      collapsed={collapsed}
                      onHandleCollapse={onHandleCollapse}
                      sideSheetStatus={sideSheetStatus}
                      toolTipArrow={toolTipArrow}
                      toolTipPosition={toolTipPosition} />
                  </Drawer>
                </Render.Else>
              </Render>
            </Hidden>
            <Hidden show='desktop'>
              <Drawer variant='permanent'
                classes={{
                  paper                 : classes.drawerPaper,
                  paperAnchorDockedLeft : classes.drawerPaperAnchorLeft

                }}
                open>
                <div className={classes.wrapper}>
                  <NavigationItem fullHeight={fullHeightIcons}
                    menuItems={menuItems}
                    isCtcAdmin={isCtcAdmin}
                    activeIndex={activeIndex}
                    overrideStyle={overrideStyle}
                    selectedIndex={selectedIndex}
                    hasMultipleSubModule={hasMultipleSubModule}
                    onSetPage={onSetPage}
                    toggleSideSheet={toggleSideSheet}
                    sideSheetStatus={sideSheetStatus}
                    secondaryNavOpen={showSecondaryNavigation}
                    closeSecondaryNavigation={closeSecondaryNavigation}
                    isInitialized={isInitialized}
                    currentPage={currentPage}
                    isLoaded={isLoaded}
                    useExpandCollapseFeature={useExpandCollapseFeature}
                    expandClasses={expandClasses}
                    collapsed={collapsed}
                    onHandleCollapse={onHandleCollapse}
                    isSideSheetArrowVisible={isSideSheetArrowVisible}
                    toolTipArrow={toolTipArrow}
                    toolTipPosition={toolTipPosition} />
                  {!useExpandCollapseFeature && <NavigationAccent />}
                </div>
              </Drawer>
            </Hidden>
          </nav>
          {isSideSheetValid &&
      <SideSheet sideSheetStatus={sideSheetStatus}
        menuItems={menuItems}
        selectedIndex={selectedIndex}
        toggleSideSheet={toggleSideSheet}
        geoFencePanelOpen={geoFencePanelOpen}
        showFindNearByPopup={showFindNearByPopup}
        findButtonStatus={findButtonStatus}
        isInitialized={isInitialized}
        isLoaded={isLoaded}
        sideSheetView={sideSheetView}
        sideSheetMobileSwipeUp={sideSheetMobileSwipeUp}
        fullHeight={fullHeight}
        isMobileFilterActive={isMobileFilterActive}>
        {sideSheetContent?.()}
      </SideSheet>}
        </>
      )}
    </div>
  );
};

export default memo(Navigation);
